/**
 * Auth context – user, token, login, logout, register. Provider lives in AuthProvider.tsx.
 */

import {
  createContext,
  use,
} from 'react';
import type { User, RegisterRequest } from '@lambda/shared/types';

/** Set when login returned requires2FA; user must submit TOTP, backup code, or SMS code to complete. */
export interface Pending2FA {
  tempToken: string;
  /** 'totp' = authenticator app (or backup code); 'sms' = code sent to phone */
  twoFactorMethod?: 'totp' | 'sms';
  /** Parent/tutor only — show Remember this device on the 2FA step. */
  canRememberDevice?: boolean;
  user: Pick<User, 'userId' | 'email' | 'name'>;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isLoggingOut: boolean;
  isRestored: boolean;
  /** True when nav can be shown: built-in role, or custom role with effectivePermissions loaded (or failed load). */
  permissionsResolved: boolean;
  /** Non-null when login returned requires2FA; complete sign-in with complete2FALogin(code). */
  pending2FA: Pending2FA | null;
}

export interface AuthContextValue extends AuthState {
  login: (email: string, password: string, persistSession?: boolean) => Promise<void>;
  complete2FALogin: (code: string, rememberDevice?: boolean) => Promise<void>;
  clearPending2FA: () => void;
  logout: () => Promise<void>;
  /** Clear session locally without calling logout API (e.g. on 401 or session timeout). */
  clearSession: () => void;
  register: (
    data: RegisterRequest
  ) => Promise<{ emailCode?: string; phoneCode?: string; isNewRegistration: boolean }>;
  setUser: (user: User | null) => void;
  /** Set user and access token (e.g. after verify-phone returns tokens for onboarding). Refresh token is handled via HttpOnly cookie. */
  setSession: (user: User, token: string) => void;
  /** Call after successful 2FA setup so UI and storage reflect twoFactorEnabled. */
  mark2FAEnabled: (method?: 'totp' | 'sms') => void;
  /** Exchange stored refresh token for new access (and refresh) tokens. Throws on failure (e.g. expired refresh). */
  refreshSession: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
